import { useEffect, useState } from 'react';
import { Loader2, ImageOff } from 'lucide-react';
import { expenseCaptureApi } from '../../services/expenseCaptureApi';

interface Props {
    receiptId: number;
    page?: number;
    className?: string;
    onClick?: () => void;
}

/**
 * Loads a short-lived SAS URL for a receipt page and renders it.
 * The SAS expires (~10 min) — refetched on receiptId/page change.
 */
export function ReceiptImage({ receiptId, page = 1, className, onClick }: Props) {
    const [url, setUrl] = useState<string | null>(null);
    const [state, setState] = useState<'loading' | 'ready' | 'error'>('loading');

    useEffect(() => {
        let active = true;
        setState('loading');
        setUrl(null);
        expenseCaptureApi.getFileUrl(receiptId, page)
            .then(res => { if (active) { setUrl(res.url); } })
            .catch(() => { if (active) setState('error'); });
        return () => { active = false; };
    }, [receiptId, page]);

    if (state === 'error') {
        return (
            <div className={`flex items-center justify-center bg-gray-100 text-gray-400 ${className || ''}`}>
                <ImageOff className="h-6 w-6" />
            </div>
        );
    }

    return (
        <div className={`relative overflow-hidden bg-gray-100 ${className || ''}`} onClick={onClick}>
            {state === 'loading' && (
                <div className="absolute inset-0 flex items-center justify-center text-gray-400">
                    <Loader2 className="h-6 w-6 animate-spin" />
                </div>
            )}
            {url && (
                <img
                    src={url}
                    alt="Receipt"
                    className="h-full w-full object-contain"
                    onLoad={() => setState('ready')}
                    onError={() => setState('error')}
                />
            )}
        </div>
    );
}
